import { useState } from "react";
import { useTranslation } from "react-i18next";
import Status from "../../../enums/Status";
import { Card, Col, Container, Row } from "react-bootstrap";
import BackButton from "../../../components/backButton/BackButton";
import { NewUserInviteForm } from "./NewUserInviteForm";
import IInvite from "../../../models/invite/IInvite";
import FormFieldValidation from "../../../enums/FormFieldValidation";
import { createInviteeValidation, IInviteValidation, validate } from "./Validation";
import inviteService from "../../../services/inviteService";
import { useNavigate } from "react-router-dom";
import IInvitee, { createInvitee } from "../../../models/invite/IInvitee";
import { InviteQueuedModal } from "./InviteQueuedModal";

export const Invite = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    
    const [status, setStatus] = useState<Status>(Status.NEUTRAL);    
    const [showQueuedModal, setShowQueuedModal] = useState<boolean>(false);
    const [invite, setInvite] = useState<IInvite>({
        reason: "",
        invitees: [createInvitee()]
    });

    const [validation, setValidation] = useState<IInviteValidation>({
        reason: FormFieldValidation.NOT_VALIDATED,
        invitees: [createInviteeValidation()]
    });

    const handleReasonChange = (value: string) => {
        setInvite(invite => ({ ...invite, reason: value }));
        setValidation(validation => ({ ...validation, reason: FormFieldValidation.NOT_VALIDATED }));
    };

    const handleInviteeChange = <K extends keyof IInvitee>(index: number, prop: K, value: IInvitee[K]) => {
        setInvite(invite => ({
            ...invite,
            invitees: invite.invitees.map((invitee, i) => i === index
                ? { ...invitee, [prop]: value }
                : invitee
            )
        }));

        setValidation(validation => ({
            ...validation,
            invitees: validation.invitees.map(
                (inviteeValidation, i) => i === index
                    ? { ...inviteeValidation, [prop]: FormFieldValidation.NOT_VALIDATED }
                    : inviteeValidation
            )
        }));
    };

    const handleInviteeAdded = () => {
        setInvite(invite => ({ ...invite, invitees: [...invite.invitees, createInvitee()] }));
        setValidation(validation => ({ ...validation, invitees: [...validation.invitees, createInviteeValidation()] }));
    };

    const handleInviteeRemoved = (index: number) => {
        setInvite(invite => {
            if (invite.invitees.length <= 1) {
                return invite;
            }

            return { ...invite, invitees: invite.invitees.filter( (_, i) => i !== index) };
        });

        setValidation(validation => {
            if (validation.invitees.length <= 1) {
                return validation;
            }

            return { ...validation, invitees: validation.invitees.filter((_, i) => i !== index) };
        });
    };

    const handleSubmit = async () => {
        if (!validate(invite, setValidation)) {
            return;
        }

        setStatus(Status.SAVING);

        try {
            const isInvited = await inviteService.inviteUsers(invite);

            if (isInvited) {
                setStatus(Status.SAVE_SUCCESS);
                setShowQueuedModal(true);
            } else {
                setStatus(Status.SAVE_ERROR);
            }
        } catch {
            setStatus(Status.SAVE_ERROR);
        }
    };

    const handleQueuedModalClosed = () => {
        setShowQueuedModal(false);
        navigate("/it/invites");
    };

    return (
        <Container className="py-3">
            <Row className="justify-content-center">
                <Col lg={9}>
                    <Card className="shadow">
                        <Card.Body>
                            <Card.Title className="d-flex align-items-center mb-3">
                                <BackButton path="/it/invites" />
                                <span>{t("it.invite.title")}</span>
                            </Card.Title>
                            
                            <NewUserInviteForm
                                handleInviteeAdded={handleInviteeAdded}
                                handleInviteeRemoved={handleInviteeRemoved}
                                handleInviteeChange={handleInviteeChange}
                                handleReasonChange={handleReasonChange}
                                handleSubmit={handleSubmit}
                                status={status}
                                invite={invite}
                                validation={validation}
                                />

                            <InviteQueuedModal show={showQueuedModal} onClose={handleQueuedModalClosed} />

                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
}